import {
  useQueries,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';
import { type Permission } from '~/types/permission';

import { vestigasApi } from '~/services/kyClient';

import { type PermissionGrantEntityValuesType } from '~/models/masterdata/PermissionGrant';

import { queryKeysUser } from './queryKeys';

type GrantedPermission = {
  id: number;
  targetId: string;
  targetName: string;
  targetType: PermissionGrantEntityValuesType;
  permissions: Permission[];
};

type ParentOrganizationalUnit = {
  id: UUID;
  name: string;
};

export type User = {
  id: UUID;
  companyId: UUID;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isSynced?: boolean;
  position: string;
  permittedSignatureTypes: string[];
  isVestigasSupport: boolean;
  parentOrganizationalUnits: ParentOrganizationalUnit[];
  parentOrganizationalUnitPaths: ParentOrganizationalUnit[][];
  orgUnits: UUID[];
  orgUnitPaths: UUID[][];
  userGroups: UUID[];
  grantedPermissions: GrantedPermission[];
  permissionsOn: GrantedPermission[];
  permissionsFrom: unknown[];
};

/**
 * Fetches user data from the API.
 * @param userId - The ID of the user to fetch.
 * @returns The user data if successful, null otherwise.
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/get_user_by_id_user__user_id__get
 */
export const fetchUser = async (userId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.USER.GET(userId))
    .json<User>();

  return response;
};

/**
 * Generates the query options for the user query.
 *
 * @param userId - The ID of the user to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getUserQueryOptions = <TSelected>({
  userId,
  options,
}: {
  userId: UUID;
  options?: Omit<
    UseQueryOptions<User, Error, TSelected>,
    'queryKey' | 'queryFn'
  >;
}) => ({
  queryFn: async () => fetchUser(userId),
  queryKey: queryKeysUser.get(userId),
  ...options,
});

/**
 * React Query based custom hook for getting the data for a user with a given userId.
 * @param userId - The ID of the user to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryUser = <TSelected = User>(
  userId: UUID,
  options?: Omit<
    UseQueryOptions<User, Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getUserQueryOptions<TSelected>({ options, userId }));

/**
 * React Query based custom hook fetching multiple users using React Query's `useQueries`.
 * @param userIds - Array of user IDs.
 * @param options - Additional options for each user query.
 * @param combine - Function to combine the query results.
 * @returns Query results.
 */
export const useQueriesUsers = <TSelected = User | undefined>(
  userIds: UUID[],
  options?: Omit<
    UseQueryOptions<User | undefined, Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
  combine?: (results: Array<UseQueryResult<TSelected>>) => unknown,
) =>
  useQueries({
    combine,
    queries: userIds.map((userId) => ({
      enabled: Boolean(userId),
      queryFn: async () => fetchUser(userId),
      queryKey: queryKeysUser.get(userId),
      ...options,
    })),
  });
